import { faMessage, faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { useContext, useEffect, useState } from 'react'
import z from 'zod'
import { useNewEmailMutation } from '~/api/mutations/action'
import { useApplicantsHead } from '~/api/queries/applicant'
import { useCompany } from '~/api/queries/company'
import { useBrandInfos } from '~/api/queries/global'
import { useOrder } from '~/api/queries/order'
import { useMeInfos } from '~/api/queries/user'
import {
  Checkbox,
  Dropdown,
  EditorInput,
  Form,
  InputCol,
  InputLine,
  Submit,
  TextInput
} from '~/components/Form'
import { SubmitAlignment } from '~/components/Form/Submit'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import { alertLink } from '~/components/Global/Interactions/Alert/Alert.css'
import { ButtonVariation } from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/Toast'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import { EmailTemplateSelector } from '~/components/Scopes/Actions/Components/EmailTemplateSelector'
import { classicLink } from '~/styles/app.css'
import { toast } from '~/utils'
import { AsideActionContext } from '~/utils/contextes'
import { EmailApplicantAttachmentsSelector } from './Components/EmailApplicantAttachmentsSelector'
import { useCloseAction } from './hooks/useCloseAction'

const formSchema = z.object({
  subject: z.string({ message: 'Le sujet ne peut pas être vide' }),
  message: z.string({ message: 'Le message ne peut pas être vide' }),
  attachments: z
    .array(
      z.object({
        fromTemplate: z.boolean(),
        object: z.object({
          filename: z.string().optional(),
          base64_content: z.string().optional()
        })
      })
    )
    .optional(),
  templateId: z.number().optional()
})

type FormValues = z.infer<typeof formSchema>

type EmailActionProps = {
  applicantIds?: number[]
  contactsIds?: number[]
  removeApplicantIdFromList?: (applicantId: number) => void
  onClose?: () => void
  orderId?: number
  sendTo?: 'applicant' | 'contact'
}

export const EmailAction = ({
  applicantIds,
  contactsIds,
  removeApplicantIdFromList,
  onClose,
  orderId,
  sendTo
}: EmailActionProps) => {
  const { data: applicantDataHead, isLoading: isApplicantLoading } =
    useApplicantsHead({
      applicantIds: applicantIds ?? [],
      additionalFields: sendTo === 'contact' ? ['attachments'] : undefined
    })
  const { data: brandInfos } = useBrandInfos()
  const [error, setError] = useState<string | null>(null)
  const [currentApplicantIds, setCurrentApplicantIds] = useState<number[]>(
    applicantIds ?? []
  )
  const [optionsValues, setOptionsValues] = useState<{
    notificationReceived: boolean
    notificationRead: boolean
  }>({
    notificationReceived: false,
    notificationRead: false
  })
  const closeAction = useCloseAction()
  const { setIsDirty } = useContext(AsideActionContext)
  const [applicantMissEmail, setApplicantMissEmail] = useState<number[]>([])
  const [attachmentsIds, setAttachmentsIds] = useState<number[]>([])

  const userInfo = useMeInfos()
  const newEmailMutation = useNewEmailMutation()

  const { data: orderData, isLoading: isOrderLoading } = useOrder(
    sendTo === 'contact' ? orderId : undefined
  )
  const companyId = orderData?.company?.id
  const { data: companyData } = useCompany(companyId)
  const defaultContact = orderData?.contact.match(/contacts\/(\d+)/)?.[1]

  let formSchemaWithContext = formSchema
  if (sendTo === 'contact') {
    formSchemaWithContext = formSchema.extend({
      contacts: z
        .array(z.string())
        .min(1, 'Au moins un destinataire est requis')
    })
  }

  // Handle applicantIds change from parent if new action is triggered
  useEffect(() => {
    if (applicantIds) {
      setCurrentApplicantIds(applicantIds)
    }
  }, [applicantIds])

  const onActionClose = () => {
    closeAction().then((confirm) => {
      if (confirm) {
        onClose?.()
      }
    })
  }

  const onSubmit = async (values: FormValues & { contacts?: number[] }) => {
    setError(null)
    setApplicantMissEmail([])
    await newEmailMutation.mutateAsync(
      {
        applicants: currentApplicantIds,
        contacts: sendTo === 'contact' ? values.contacts : undefined,
        emailTo: sendTo ?? 'applicant',
        templateId: values.templateId,
        subject: values.subject,
        content: values.message,
        notificationRead: optionsValues.notificationRead,
        notificationReceived: optionsValues.notificationReceived,
        attachmentsIds,
        attachments: values.attachments
          ?.filter(
            (attachment) =>
              !attachment.fromTemplate &&
              attachment.object?.base64_content &&
              attachment.object?.filename
          )
          .map(
            (attachment) =>
              attachment.object as {
                base64_content: string
                filename: string
              }
          ),
        orderId: orderId
      },
      {
        onSuccess: () => {
          closeAction(true)
          onClose?.()
          toast('Email envoyé', "L'email a bien été envoyé", ToastType.Success)
        },
        onError: (error) => {
          const potentialApplicantMissEmail = error.response?.message?.match(
            /Applicant (\d+) does not have an email address/i
          )?.[1]
          if (potentialApplicantMissEmail) {
            setApplicantMissEmail([
              Number.parseInt(potentialApplicantMissEmail)
            ])
          }
          const potentialApplicantUnvalidEmail = error.response?.message?.match(
            /Applicant (\d+) does not have a valid email address/i
          )?.[1]
          if (potentialApplicantUnvalidEmail) {
            setApplicantMissEmail([
              Number.parseInt(potentialApplicantUnvalidEmail)
            ])
          }
          const potentialContactMissEmail = error.response?.message?.match(
            /Contact (\d+) does not have an email address/i
          )?.[1]
          if (potentialContactMissEmail) {
            setError(
              'Veuillez vérifier que les contacts ont bien une adresse email'
            )
            return
          }
          setError("Une erreur est survenue lors de l'envoi de l'email")
        }
      }
    )
  }

  let content = <Loader />

  if (
    userInfo &&
    !isOrderLoading &&
    (sendTo !== 'contact' || !isApplicantLoading)
  ) {
    const fromEmail = [
      {
        label: `${userInfo.firstname} ${userInfo.lastname}`,
        value: `user:${userInfo.id}`
      }
    ]
    for (const agency of userInfo.agencies) {
      fromEmail.push({
        label: agency.name,
        value: `agency:${agency.id}`
      })
    }

    let prepend = <></>
    if (
      sendTo === 'contact' &&
      applicantDataHead &&
      applicantDataHead.length > 0
    ) {
      prepend = (
        <EmailApplicantAttachmentsSelector
          applicantDataHead={applicantDataHead}
          setAttachmentsIds={setAttachmentsIds}
          attachmentsIds={attachmentsIds}
        />
      )
    }

    content = (
      <Form
        onSubmit={onSubmit}
        schema={formSchemaWithContext}
        globalError={error}
        globalErrorInSubmit={true}
        onDirtyStateChange={(isDirty) => {
          if (isDirty) {
            setIsDirty(true)
          }
        }}
      >
        <InputCol>
          <InputLine>
            <Dropdown
              name="sendFrom"
              options={fromEmail}
              defaultValue={`user:${userInfo.id}`}
              label="De"
              onChange={() => setIsDirty(true)}
              required
            />
            <EmailTemplateSelector
              name="templateId"
              subjectFieldName="subject"
              contentFieldName="message"
              onChange={() => setIsDirty(true)}
            />
          </InputLine>

          {companyData && orderData && (
            <Dropdown
              name="contacts"
              label="Pour"
              defaultValues={defaultContact ? [defaultContact] : []}
              options={
                companyData?.contacts
                  ?.filter((contact) => !contact.archived)
                  ?.map((contact) => ({
                    label: `${contact.firstname} ${contact.lastname}`,
                    value: contact.id.toString()
                  })) || []
              }
              placeholder="Sélectionner un ou plusieurs contacts"
              multiple={true}
              onChange={() => setIsDirty(true)}
            />
          )}
          <TextInput name="subject" placeholder="Sujet" required />
          <EditorInput
            name="message"
            prepend={prepend}
            rows={5}
            required
            hasAttachments
            placeholderCategories={
              orderId
                ? ['Candidat', 'Contact', 'Entreprise', 'Autres', 'Commande']
                : ['Candidat', 'Autres']
            }
            onChange={() => setIsDirty(true)}
            options={
              <InputCol>
                <Checkbox
                  defaultChecked={false}
                  label="Demander un accusé de réception"
                  onChange={(checked) => {
                    setOptionsValues((prev) => ({
                      ...prev,
                      notificationReceived: checked
                    }))
                    setIsDirty(true)
                  }}
                />
                <Checkbox
                  defaultChecked={false}
                  label="Demander une confirmation de lecture"
                  onChange={(checked) => {
                    setOptionsValues((prev) => ({
                      ...prev,
                      notificationRead: checked
                    }))
                    setIsDirty(true)
                  }}
                />
              </InputCol>
            }
          />
          {applicantMissEmail.map((applicantId) => {
            const applicantData = applicantDataHead?.find(
              (applicant) => applicant.id === applicantId
            )
            return (
              <Alert
                key={applicantId}
                variation={AlertVariation.Danger}
                message={`Le candidat ${applicantData?.firstname} ${applicantData?.lastname} n'a pas d'adresse email valide.`}
                onDismiss={onActionClose}
              >
                <div>
                  <span
                    className={clsx(classicLink, alertLink)}
                    onClick={(e) => {
                      e.preventDefault()
                      window.open(
                        `${brandInfos?.legacyUrl}/admin/candidats/edit/${applicantId}`
                      )
                    }}
                    onAuxClick={(e) => {
                      e.preventDefault()
                      window.open(
                        `${brandInfos?.legacyUrl}/admin/candidats/edit/${applicantId}`
                      )
                    }}
                  >
                    Modifier le candidat
                  </span>
                  {removeApplicantIdFromList &&
                    (currentApplicantIds?.length ?? 0) > 1 && (
                      <span
                        className={clsx(classicLink, alertLink)}
                        onClick={(e) => {
                          e.preventDefault()
                          removeApplicantIdFromList(applicantId)
                          setCurrentApplicantIds((prev) =>
                            prev.filter((id) => id !== applicantId)
                          )
                          setApplicantMissEmail((prev) =>
                            prev.filter((id) => id !== applicantId)
                          )
                        }}
                      >
                        Retirer de l'envoi
                      </span>
                    )}
                  {currentApplicantIds?.length === 1 && onClose && (
                    <span
                      className={clsx(classicLink, alertLink)}
                      onClick={onActionClose}
                    >
                      Annuler l'envoi
                    </span>
                  )}
                </div>
              </Alert>
            )
          })}
          <Submit
            buttonVariation={ButtonVariation.Primary}
            loadingText="Envoi..."
            successText="Envoyé !"
            errorText="Erreur de saisie"
            onSuccessReset={false}
            submitAlignment={SubmitAlignment.Right}
          >
            <FontAwesomeIcon icon={faPaperPlaneTop} />
            Envoyer l'email
          </Submit>
        </InputCol>
      </Form>
    )
  }

  return (
    <AsideAction
      title={`Envoyer un email${
        sendTo === 'applicant'
          ? ` au${currentApplicantIds.length > 1 ? 'x' : ''} candidat${currentApplicantIds.length > 1 ? 's' : ''}`
          : ' au(x) contact(s)'
      }`}
      icon={faMessage}
      applicantIds={sendTo === 'applicant' ? (currentApplicantIds ?? []) : []}
      onClose={onClose}
    >
      {content}
    </AsideAction>
  )
}
